import { useCallback, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent, RefObject } from 'react'

/**
 * **Die Grenze zwischen zwei Bereichen eines Arbeitsfensters** — 7.2
 * Abschnitt 41, R1262, R1263.
 *
 * Der Befund vom 2026-10-06: Links im Fenster *Konten dieser Lieferung
 * zuordnen* stand *„Forderungen Fensterprobe Sonderfa…"*, rechts war Platz.
 * Die Aufteilung 40 zu 60 passt für viele Lieferungen und für manche nicht —
 * welcher Fall vorliegt, sieht der Anwender, nicht das Programm.
 *
 * **Gemerkt wird im Browser**, wie die Breite einer Tabellenspalte: Sie sagt
 * etwas über den Bildschirm, vor dem einer sitzt, nicht über die Zahlen.
 */

const ABLAGE = 'coko.bereichsgrenze.'

/** Jeder Bereich behält mindestens ein Fünftel (R1263). */
export const MINDESTANTEIL = 0.2

/** Ein Pfeiltastendruck verschiebt um so viel (R306). */
const SCHRITT = 0.02

function begrenzt(anteil: number): number {
  return Math.min(1 - MINDESTANTEIL, Math.max(MINDESTANTEIL, anteil))
}

function gelesen(kennung: string): number | null {
  try {
    const roh = window.localStorage.getItem(ABLAGE + kennung)
    const zahl = roh === null ? NaN : Number(roh)
    return Number.isFinite(zahl) ? begrenzt(zahl) : null
  } catch {
    // Ein Browser ohne Speicher ist kein Fehlerfall: Dann gilt die Vorgabe.
    return null
  }
}

function geschrieben(kennung: string, anteil: number | null): void {
  try {
    if (anteil === null) {
      window.localStorage.removeItem(ABLAGE + kennung)
    } else {
      window.localStorage.setItem(ABLAGE + kennung, String(Math.round(anteil * 1000) / 1000))
    }
  } catch {
    /* siehe oben */
  }
}

/**
 * Der Anteil des linken Bereichs — gemerkt je `kennung`, sonst die Vorgabe.
 *
 * @returns Der Anteil und die Spaltenvorlage für ein Raster mit drei Spalten:
 *   links, die Grenze, rechts.
 */
export function useBereichsgrenze(kennung: string, vorgabe = 0.4) {
  const [anteil, setAnteil] = useState(() => gelesen(kennung) ?? vorgabe)

  const setze = useCallback(
    (neu: number | null) => {
      const wert = neu === null ? vorgabe : begrenzt(neu)
      setAnteil(wert)
      geschrieben(kennung, neu === null ? null : wert)
    },
    [kennung, vorgabe],
  )

  return {
    anteil,
    setze,
    spalten: `minmax(0, ${anteil}fr) auto minmax(0, ${1 - anteil}fr)`,
  }
}

/**
 * Der Griff auf der Grenze: ziehen mit der Maus, verschieben mit den
 * Pfeiltasten, Doppelklick stellt die Vorgabe wieder her (R1262, R306).
 */
export function Bereichsgrenze({
  rahmen,
  anteil,
  setze,
  beschriftung,
}: {
  /** Das Raster, dessen Breite 100 % ist. */
  rahmen: RefObject<HTMLElement | null>
  anteil: number
  setze: (anteil: number | null) => void
  /** Was die Grenze trennt — für den, der sie nicht sieht. */
  beschriftung: string
}) {
  const [zieht, setZieht] = useState(false)
  const griff = useRef<HTMLDivElement>(null)

  function anteilBei(x: number): number | null {
    const r = rahmen.current?.getBoundingClientRect()
    return r && r.width > 0 ? (x - r.left) / r.width : null
  }

  function beiDruck(e: PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    griff.current?.setPointerCapture?.(e.pointerId)
    setZieht(true)
  }

  function beiZug(e: PointerEvent<HTMLDivElement>) {
    if (!zieht) {
      return
    }
    const neu = anteilBei(e.clientX)
    if (neu !== null && Number.isFinite(neu)) {
      setze(neu)
    }
  }

  function beiLoslassen(e: PointerEvent<HTMLDivElement>) {
    griff.current?.releasePointerCapture?.(e.pointerId)
    setZieht(false)
  }

  function beiTaste(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      setze(anteil + (e.key === 'ArrowLeft' ? -SCHRITT : SCHRITT))
    }
  }

  return (
    <div
      ref={griff}
      role="separator"
      aria-orientation="vertical"
      aria-label={beschriftung}
      aria-valuemin={Math.round(MINDESTANTEIL * 100)}
      aria-valuemax={Math.round((1 - MINDESTANTEIL) * 100)}
      aria-valuenow={Math.round(anteil * 100)}
      tabIndex={0}
      className={zieht ? 'bereichsgrenze zieht' : 'bereichsgrenze'}
      onPointerDown={beiDruck}
      onPointerMove={beiZug}
      onPointerUp={beiLoslassen}
      onPointerCancel={beiLoslassen}
      onKeyDown={beiTaste}
      onDoubleClick={() => setze(null)}
    />
  )
}
